import { useState, useRef, useEffect } from 'react'
import { Routes, Route, useNavigate, useLocation, Navigate, Link, Outlet } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { adminApi, type AdminResponse } from '../api/admin'
import Button from '../components/Button'
import DataTable from '../components/DataTable'
import Modal from '../components/Modal'
import type { AdminConfig, Question } from '../types'
import {
  LayoutDashboard,
  FileText,
  ClipboardList,
  Settings,
  LogOut,
  Upload,
  RefreshCw,
  Trash2,
  Edit2,
  Eye,
  EyeOff,
  Download,
  Plus,
  Save,
  FileSearch,
  Sliders,
  Flag,
  Archive,
  AlertTriangle,
  BarChart3,
  Play,
  Bot,
  User,
  BookOpen,
  Activity,
} from 'lucide-react'
import type { BenchmarkRun, BenchmarkCategory, BulkPmcRun, BulkPmcItem } from '../api/admin'
import UsageTab from '../components/admin/UsageTab'

// ---------- Helpers ----------
function fmtErr(e: any, fallback: string): string {
  const detail = e?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) return detail.map((d: any) => d.msg || String(d)).join('; ') || fallback
  return fallback
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function useTabRoute(base: string) {
  const location = useLocation()
  const path = location.pathname.replace(base, '').replace(/^\//, '') || 'responses'
  return path
}

// ---------- Login ----------
function LoginForm() {
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)
    try {
      const res = await adminApi.login(password)
      login(res.access_token)
      navigate('/admin/responses')
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Invalid password')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl border shadow-sm p-8">
        <h2 className="text-2xl font-bold mb-6">Admin Login</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
              autoFocus
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <Button type="submit" isLoading={isLoading} className="w-full">
            Login
          </Button>
        </form>
      </div>
    </div>
  )
}

// ---------- Responses Tab ----------
function ResponsesTab() {
  const [responses, setResponses] = useState<AdminResponse[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [editing, setEditing] = useState<AdminResponse | null>(null)
  const [editJson, setEditJson] = useState('')
  const [search, setSearch] = useState('')
  const [publishedFilter, setPublishedFilter] = useState<'all' | 'yes' | 'no'>('all')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const fileRef = useRef<HTMLInputElement>(null)

  const load = async () => {
    setIsLoading(true)
    setError('')
    try {
      const data = await adminApi.listResponses()
      setResponses(data)
      setSelected(new Set())
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to load responses'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const filtered = responses.filter((r) => {
    const term = search.trim().toLowerCase()
    const matchesSearch =
      !term ||
      r.file_id.toLowerCase().includes(term) ||
      r.code.toLowerCase().includes(term) ||
      String((r.fields as any).Title || (r.fields as any).title || '').toLowerCase().includes(term)
    const matchesPublished =
      publishedFilter === 'all' ||
      (publishedFilter === 'yes' && r.is_published) ||
      (publishedFilter === 'no' && !r.is_published)
    return matchesSearch && matchesPublished
  })

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allFilteredSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id))
  const toggleSelectAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (allFilteredSelected) filtered.forEach((r) => next.delete(r.id))
      else filtered.forEach((r) => next.add(r.id))
      return next
    })
  }

  const handleDelete = async (row: AdminResponse) => {
    if (!confirm(`Delete response ${row.file_id}? This cannot be undone.`)) return
    setError('')
    try {
      await adminApi.deleteResponse(row.id)
      setMessage(`Deleted ${row.file_id}`)
      load()
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to delete response'))
    }
  }

  const handleBulkDelete = async () => {
    if (selected.size === 0) return
    if (!confirm(`Delete ${selected.size} selected response(s)? This cannot be undone.`)) return
    setError('')
    try {
      await Promise.all(Array.from(selected).map((id) => adminApi.deleteResponse(id)))
      setMessage(`Deleted ${selected.size} response(s)`)
      load()
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to delete selected responses'))
    }
  }

  const handleDownload = async () => {
    setError('')
    try {
      const blob = await adminApi.downloadResponses()
      saveBlob(blob, 'responses.csv')
    } catch (e: any) {
      setError(fmtErr(e, 'Download failed'))
    }
  }

  const startEdit = (row: AdminResponse) => {
    setEditing(row)
    setEditJson(JSON.stringify(row.fields, null, 2))
  }

  const saveEdit = async () => {
    if (!editing) return
    let parsed: Record<string, unknown>
    try {
      parsed = JSON.parse(editJson)
    } catch {
      setError('Invalid JSON in fields')
      return
    }
    setError('')
    try {
      await adminApi.updateResponse(editing.id, { fields: parsed, is_published: editing.is_published })
      setMessage(`Updated ${editing.file_id}`)
      setEditing(null)
      load()
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to update response'))
    }
  }

  const handleUpload = async (file: File) => {
    setError('')
    try {
      await adminApi.uploadResponses(file)
      setMessage('Responses uploaded')
      load()
    } catch (e: any) {
      setError(fmtErr(e, 'Upload failed'))
    } finally {
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const columns = [
    {
      key: 'select',
      header: (
        <input
          type="checkbox"
          checked={allFilteredSelected}
          onChange={toggleSelectAllFiltered}
          className="h-4 w-4 rounded border-gray-300"
          aria-label="Select all"
        />
      ),
      className: 'w-10',
      cell: (row: AdminResponse) => (
        <input
          type="checkbox"
          checked={selected.has(row.id)}
          onChange={() => toggleSelected(row.id)}
          className="h-4 w-4 rounded border-gray-300"
          aria-label={`Select ${row.file_id}`}
        />
      ),
    },
    { key: 'file_id', header: 'File ID', cell: (row: AdminResponse) => <span className="font-mono">{row.file_id}</span> },
    { key: 'code', header: 'Code', cell: (row: AdminResponse) => <span className="font-mono">{row.code}</span> },
    {
      key: 'source',
      header: 'Source',
      cell: (row: AdminResponse) =>
        row.is_ai_submitted ? (
          <span className="inline-flex items-center gap-1 text-xs text-purple-700 bg-purple-100 px-2 py-1 rounded-full" title="AI auto-submitted">
            <Bot className="w-3 h-3" />
            AI
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full" title="Human submitted">
            <User className="w-3 h-3" />
            Human
          </span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row: AdminResponse) => (
        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${row.status === 'submitted' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
          {row.status}
        </span>
      ),
    },
    {
      key: 'published',
      header: 'Published',
      cell: (row: AdminResponse) => (row.is_published ? 'Yes' : 'No'),
    },
    {
      key: 'submitted',
      header: 'Submitted',
      cell: (row: AdminResponse) => (row.submitted_at ? new Date(row.submitted_at).toLocaleDateString() : '—'),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'whitespace-nowrap',
      cell: (row: AdminResponse) => (
        <div className="flex items-center gap-2">
          <button onClick={() => startEdit(row)} className="p-1 text-blue-600 hover:bg-blue-50 rounded">
            <Edit2 className="w-4 h-4" />
          </button>
          <button onClick={() => handleDelete(row)} className="p-1 text-red-600 hover:bg-red-50 rounded">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
      {message && <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm">{message}</div>}
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={fileRef}
          type="file"
          accept=".csv"
          onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
          className="hidden"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search file ID / code / title…"
          className="border rounded-lg px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        <select
          value={publishedFilter}
          onChange={(e) => setPublishedFilter(e.target.value as 'all' | 'yes' | 'no')}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          <option value="all">All</option>
          <option value="yes">Published</option>
          <option value="no">Unpublished</option>
        </select>
        <Button variant="secondary" size="sm" onClick={handleDownload}>
          <Download className="w-4 h-4 mr-1" />
          Download CSV
        </Button>
        <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
          <Upload className="w-4 h-4 mr-1" />
          Upload CSV
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={handleBulkDelete}
          disabled={selected.size === 0}
        >
          <Trash2 className="w-4 h-4 mr-1" />
          Delete selected ({selected.size})
        </Button>
        <span className="text-sm text-gray-500 ml-auto">{filtered.length} of {responses.length}</span>
      </div>
      <DataTable
        columns={columns}
        rows={filtered}
        keyExtractor={(row) => row.id}
        isLoading={isLoading}
        emptyMessage="No responses found."
      />
      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={`Edit ${editing?.file_id}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={saveEdit}>Save</Button>
          </>
        }
      >
        {editing && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Published</label>
              <select
                value={editing.is_published ? 'true' : 'false'}
                onChange={(e) => setEditing({ ...editing, is_published: e.target.value === 'true' })}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fields (JSON)</label>
              <textarea
                value={editJson}
                onChange={(e) => setEditJson(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 font-mono text-xs"
                rows={12}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

// ---------- Questionnaire Tab ----------
function QuestionnaireTab() {
  const [questions, setQuestions] = useState<Question[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<Partial<Question>>({})
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const fileRef = useRef<HTMLInputElement>(null)

  const load = async () => {
    setIsLoading(true)
    setError('')
    try {
      const data = await adminApi.getQuestionnaire()
      setQuestions(data)
      setSelected(new Set())
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to load questionnaire'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const filtered = questions.filter((q) => {
    const term = search.trim().toLowerCase()
    if (!term) return true
    return (
      q.input_id.toLowerCase().includes(term) ||
      String(q.question).toLowerCase().includes(term) ||
      String(q.page).toLowerCase().includes(term)
    )
  })

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allFilteredSelected = filtered.length > 0 && filtered.every((q) => selected.has(q.id))
  const toggleSelectAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (allFilteredSelected) filtered.forEach((q) => next.delete(q.id))
      else filtered.forEach((q) => next.add(q.id))
      return next
    })
  }

  const handleDownload = async (format: 'csv' | 'xlsx') => {
    setError('')
    try {
      const blob = await adminApi.downloadQuestionnaire(format)
      saveBlob(blob, `questionnaire.${format}`)
    } catch (e: any) {
      setError(fmtErr(e, 'Download failed'))
    }
  }

  const handleAddRow = async () => {
    setError('')
    try {
      await adminApi.addQuestion({ page: 'Page 1', box: 'General', question: 'New question', input_type: 'text' })
      setMessage('New row added')
      load()
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to add row'))
    }
  }

  const handleBulkDelete = async () => {
    if (selected.size === 0) return
    if (!confirm(`Delete ${selected.size} selected question(s)? This cannot be undone.`)) return
    setError('')
    try {
      await Promise.all(Array.from(selected).map((id) => adminApi.deleteQuestion(id)))
      setMessage(`Deleted ${selected.size} question(s)`)
      load()
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to delete selected questions'))
    }
  }

  const startEdit = (q: Question) => {
    setEditingId(q.id)
    setEditForm({ ...q })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditForm({})
  }

  const saveEdit = async () => {
    if (!editForm.id) return
    const next = questions.map((q) => (q.id === editForm.id ? ({ ...q, ...editForm } as Question) : q))
    setError('')
    try {
      await adminApi.updateQuestionnaire(next)
      setMessage(`Updated question ${editForm.id}`)
      setQuestions(next)
      cancelEdit()
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to update question'))
    }
  }

  const handleUpload = async (file: File) => {
    setError('')
    try {
      await adminApi.uploadQuestionnaire(file)
      setMessage('Questionnaire uploaded')
      load()
    } catch (e: any) {
      setError(fmtErr(e, 'Upload failed'))
    } finally {
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const columns = [
    {
      key: 'select',
      header: (
        <input
          type="checkbox"
          checked={allFilteredSelected}
          onChange={toggleSelectAllFiltered}
          className="h-4 w-4 rounded border-gray-300"
          aria-label="Select all"
        />
      ),
      className: 'w-10',
      cell: (row: Question) => (
        <input
          type="checkbox"
          checked={selected.has(row.id)}
          onChange={() => toggleSelected(row.id)}
          className="h-4 w-4 rounded border-gray-300"
          aria-label={`Select ${row.input_id}`}
        />
      ),
    },
    { key: 'order', header: '#', cell: (row: Question) => row.order_idx, className: 'w-12' },
    { key: 'page', header: 'Page', cell: (row: Question) => row.page },
    { key: 'input_id', header: 'Input ID', cell: (row: Question) => <span className="font-mono text-xs">{row.input_id}</span> },
    { key: 'question', header: 'Question', cell: (row: Question) => row.question },
    { key: 'type', header: 'Type', cell: (row: Question) => row.input_type },
    { key: 'required', header: 'Required', cell: (row: Question) => (row.required ? 'Yes' : 'No') },
    {
      key: 'actions',
      header: 'Actions',
      className: 'whitespace-nowrap w-24',
      cell: (row: Question) => (
        <div className="flex items-center gap-2">
          <button onClick={() => startEdit(row)} className="p-1 text-blue-600 hover:bg-blue-50 rounded">
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={async () => {
              if (!confirm(`Delete question ${row.input_id}?`)) return
              try {
                await adminApi.deleteQuestion(row.id)
                setMessage(`Deleted ${row.input_id}`)
                load()
              } catch (e: any) {
                setError(fmtErr(e, 'Failed to delete question'))
              }
            }}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
      {message && <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm">{message}</div>}
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.xlsx"
          onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
          className="hidden"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search input ID / question / page…"
          className="border rounded-lg px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        <Button variant="primary" size="sm" onClick={handleAddRow}>
          <Plus className="w-4 h-4 mr-1" />
          Add row
        </Button>
        <Button variant="secondary" size="sm" onClick={() => handleDownload('csv')}>
          <Download className="w-4 h-4 mr-1" />
          CSV
        </Button>
        <Button variant="secondary" size="sm" onClick={() => handleDownload('xlsx')}>
          <Download className="w-4 h-4 mr-1" />
          Excel
        </Button>
        <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
          <Upload className="w-4 h-4 mr-1" />
          Upload CSV/XLSX
        </Button>
        <Button variant="danger" size="sm" onClick={handleBulkDelete} disabled={selected.size === 0}>
          <Trash2 className="w-4 h-4 mr-1" />
          Delete selected ({selected.size})
        </Button>
        <span className="text-sm text-gray-500 ml-auto">{filtered.length} of {questions.length}</span>
      </div>
      <DataTable
        columns={columns}
        rows={filtered}
        keyExtractor={(row) => row.id}
        isLoading={isLoading}
        emptyMessage="No questions found."
      />
      <Modal
        open={!!editingId}
        onClose={cancelEdit}
        title="Edit question"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={cancelEdit}>
              Cancel
            </Button>
            <Button onClick={saveEdit}>Save</Button>
          </>
        }
      >
        {editForm.id && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            {(
              [
                ['question', 'Question'],
                ['option', 'Option'],
                ['input_id', 'Input ID'],
                ['page', 'Page'],
                ['box', 'Box'],
                ['dependence', 'Dependence'],
                ['dependence_value', 'Dependence value'],
                ['report_section', 'Report section'],
                ['report_columns', 'Report columns'],
                ['column_width', 'Column width'],
                ['report_format', 'Report format'],
                ['report_category_1', 'Report category 1'],
                ['report_category_2', 'Report category 2'],
                ['report_display_name', 'Report display name'],
                ['merge_group_id', 'Merge group ID'],
                ['column_position', 'Column position'],
                ['report_label', 'Report label'],
                ['report_header_col1', 'Report header col1'],
                ['report_header_col2', 'Report header col2'],
                ['report_header_col3', 'Report header col3'],
                ['ai_config', 'AI config'],
              ] as [keyof Question, string][]
            ).map(([key, label]) => (
              <div key={key} className={key === 'question' ? 'md:col-span-2' : ''}>
                <label className="block font-medium text-gray-700 mb-1">{label}</label>
                <input
                  type="text"
                  value={String(editForm[key] ?? '')}
                  onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            ))}
            <div>
              <label className="block font-medium text-gray-700 mb-1">Input type</label>
              <select
                value={editForm.input_type || ''}
                onChange={(e) => setEditForm({ ...editForm, input_type: e.target.value as Question['input_type'] })}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="text">text</option>
                <option value="textarea">textarea</option>
                <option value="y/n">y/n</option>
                <option value="check">check</option>
                <option value="header">header</option>
              </select>
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-1">Required</label>
              <select
                value={editForm.required ? 'true' : 'false'}
                onChange={(e) => setEditForm({ ...editForm, required: e.target.value === 'true' })}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-1">Display in report</label>
              <select
                value={editForm.display_in_report ? 'true' : 'false'}
                onChange={(e) => setEditForm({ ...editForm, display_in_report: e.target.value === 'true' })}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div>
              <label className="block font-medium text-gray-700 mb-1">Order</label>
              <input
                type="number"
                value={editForm.order_idx ?? 0}
                onChange={(e) => setEditForm({ ...editForm, order_idx: Number(e.target.value) })}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

// ---------- Reports Tab ----------
function ReportsTab() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  const regenerate = async (scope: 'missing' | 'all') => {
    if (!confirm(`Regenerate ${scope} reports? This may take a while.`)) return
    setIsLoading(true)
    setError('')
    setMessage('')
    try {
      await adminApi.regenerateReports(scope)
      setMessage(`${scope === 'all' ? 'All' : 'Missing'} reports regeneration started.`)
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to regenerate reports'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
      {message && <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm">{message}</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <h3 className="font-semibold mb-2">Regenerate missing reports</h3>
          <p className="text-sm text-gray-600 mb-4">Generate reports only for submissions that do not yet have one.</p>
          <Button onClick={() => regenerate('missing')} isLoading={isLoading}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Regenerate missing
          </Button>
        </div>
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <h3 className="font-semibold mb-2">Regenerate all reports</h3>
          <p className="text-sm text-gray-600 mb-4">Overwrite and regenerate reports for every submission.</p>
          <Button variant="secondary" onClick={() => regenerate('all')} isLoading={isLoading}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Regenerate all
          </Button>
        </div>
      </div>
    </div>
  )
}

// ---------- Report Template Preview Tab ----------
function ReportPreviewTab() {
  const [html, setHtml] = useState<string | null>(null)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  }, [blobUrl])

  const generate = async () => {
    setIsLoading(true)
    setError('')
    try {
      const content = await adminApi.getReportPreview()
      setHtml(content)
      if (blobUrl) URL.revokeObjectURL(blobUrl)
      const url = URL.createObjectURL(new Blob([content], { type: 'text/html' }))
      setBlobUrl(url)
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to generate preview'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
      <div className="bg-white rounded-xl border shadow-sm p-6 space-y-3">
        <h3 className="font-semibold">Report Template Preview</h3>
        <p className="text-sm text-gray-600">
          Shows how an actual report will look, with every section and field present (including empty ones).
        </p>
        <div className="flex items-center gap-3">
          <Button onClick={generate} isLoading={isLoading}>
            <FileSearch className="w-4 h-4 mr-1" />
            Generate preview
          </Button>
          {blobUrl && (
            <a
              href={blobUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-800"
            >
              <FileText className="w-4 h-4" />
              Open in new tab
            </a>
          )}
        </div>
      </div>
      {html && (
        <iframe
          title="Report preview"
          srcDoc={html}
          className="w-full rounded-xl border bg-white"
          style={{ height: 800 }}
        />
      )}
    </div>
  )
}

// ---------- LLM Extraction Config Tab ----------
const CONFIG_OPTIONS = [
  { value: 'core', label: 'Core' },
  { value: 'miblood', label: 'MIBlood' },
  { value: 'miflowcyt', label: 'MIFlowCyt' },
]

function ExtractionConfigTab() {
  const [name, setName] = useState('core')
  const [columns, setColumns] = useState<string[]>([])
  const [rows, setRows] = useState<Record<string, string>[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  const load = async (cfgName: string) => {
    setIsLoading(true)
    setError('')
    setMessage('')
    try {
      const cfg = await adminApi.getExtractionConfig(cfgName)
      setColumns(cfg.columns)
      setRows(cfg.rows)
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to load config'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load(name)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name])

  const updateCell = (rowIdx: number, col: string, value: string) => {
    setRows((prev) => prev.map((r, i) => (i === rowIdx ? { ...r, [col]: value } : r)))
  }

  const addRow = () => {
    const blank: Record<string, string> = {}
    columns.forEach((c) => (blank[c] = ''))
    setRows((prev) => [...prev, blank])
  }

  const deleteRow = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx))
  }

  const save = async () => {
    setError('')
    setMessage('')
    try {
      await adminApi.saveExtractionConfig({ name, columns, rows })
      setMessage('Config saved. New extractions will use the updated prompts.')
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to save config'))
    }
  }

  const download = async () => {
    setError('')
    try {
      const blob = await adminApi.downloadExtractionConfig(name)
      saveBlob(blob, `llm_extraction_config_${name}.csv`)
    } catch (e: any) {
      setError(fmtErr(e, 'Download failed'))
    }
  }

  const upload = async (file: File) => {
    setError('')
    try {
      await adminApi.uploadExtractionConfig(name, file)
      setMessage('Config uploaded')
      load(name)
    } catch (e: any) {
      setError(fmtErr(e, 'Upload failed'))
    } finally {
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <div className="space-y-4">
      {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
      {message && <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm">{message}</div>}

      <div className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
        <p className="text-sm text-gray-600">
          Manage the prompts and settings the AI assistant uses to extract data from manuscripts. Changes are saved to a
          writable override copy and take effect on the next extraction.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm font-medium text-gray-700">Config file:</label>
          <select
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            {CONFIG_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
            className="hidden"
          />
          <Button variant="primary" size="sm" onClick={addRow}>
            <Plus className="w-4 h-4 mr-1" />
            Add row
          </Button>
          <Button variant="secondary" size="sm" onClick={download}>
            <Download className="w-4 h-4 mr-1" />
            Download CSV
          </Button>
          <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
            <Upload className="w-4 h-4 mr-1" />
            Upload CSV
          </Button>
          <Button size="sm" onClick={save}>
            <Save className="w-4 h-4 mr-1" />
            Save changes
          </Button>
          <span className="text-sm text-gray-500 ml-auto">{rows.length} rows</span>
        </div>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-gray-500">Loading…</div>
      ) : (
        <div className="bg-white rounded-xl border shadow-sm overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((c) => (
                  <th key={c} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                    {c}
                  </th>
                ))}
                <th className="px-3 py-2 w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((row, idx) => (
                <tr key={idx} className="align-top">
                  {columns.map((c) => {
                    const isLong = c.toLowerCase().includes('prompt') || c.toLowerCase().includes('text')
                    return (
                      <td key={c} className="px-2 py-1">
                        {isLong ? (
                          <textarea
                            value={row[c] ?? ''}
                            onChange={(e) => updateCell(idx, c, e.target.value)}
                            rows={2}
                            className="w-80 border rounded px-2 py-1 text-xs"
                          />
                        ) : (
                          <input
                            type="text"
                            value={row[c] ?? ''}
                            onChange={(e) => updateCell(idx, c, e.target.value)}
                            className="w-40 border rounded px-2 py-1 text-xs"
                          />
                        )}
                      </td>
                    )
                  })}
                  <td className="px-2 py-1">
                    <button onClick={() => deleteRow(idx)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={Math.max(1, columns.length + 1)} className="px-4 py-8 text-center text-gray-500">
                    No rows.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ---------- Settings Tab ----------
// Build the full model registry for a provider: the active model first, then
// the saved extras, de-duplicated. Mirrors the backend _models_for_provider.
function parseModelList(activeModel: string, extra: string): string[] {
  const all = [activeModel, ...(extra || '').split(/[,\n]/)].map((s) => s.trim()).filter(Boolean)
  return Array.from(new Set(all))
}

function ModelManager({
  title,
  list,
  activeModel,
  disabled,
  onChange,
}: {
  title: string
  list: string[]
  activeModel: string
  disabled?: boolean
  onChange: (list: string[], activeModel: string) => void
}) {
  const [draft, setDraft] = useState('')
  const add = () => {
    const name = draft.trim()
    if (!name) return
    if (!list.includes(name)) onChange([...list, name], activeModel || name)
    setDraft('')
  }
  const remove = (m: string) => {
    const next = list.filter((x) => x !== m)
    onChange(next, m === activeModel ? next[0] || '' : activeModel)
  }
  return (
    <div className="border rounded-lg p-3 space-y-2">
      <label className="block text-sm font-medium text-gray-700">{title}</label>
      <div>
        <span className="block text-xs text-gray-500 mb-1">
          Active model — used for text extraction and as the benchmark default
        </span>
        <select
          value={activeModel}
          onChange={(e) => onChange(list, e.target.value)}
          disabled={disabled || list.length === 0}
          className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
        >
          {list.length === 0 && <option value="">(no models yet — add one below)</option>}
          {list.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>
      {list.length > 0 && (
        <ul className="space-y-1">
          {list.map((m) => (
            <li key={m} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1 text-sm">
              <span>
                {m}
                {m === activeModel && <span className="ml-2 text-xs text-brand-600 font-medium">active</span>}
              </span>
              <button
                type="button"
                onClick={() => remove(m)}
                disabled={disabled}
                className="text-gray-400 hover:text-red-600 disabled:opacity-40"
                aria-label={`Delete ${m}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add()
            }
          }}
          placeholder="Add a model name…"
          disabled={disabled}
          className="flex-1 border rounded-lg px-3 py-2 text-sm"
        />
        <Button size="sm" variant="secondary" onClick={add} disabled={disabled || !draft.trim()}>
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>
    </div>
  )
}

function SettingsTab() {
  const [config, setConfig] = useState<AdminConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [showKeys, setShowKeys] = useState(false)
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' })
  const [pwdLoading, setPwdLoading] = useState(false)

  const load = async () => {
    setIsLoading(true)
    try {
      const data = await adminApi.getConfig()
      setConfig(data)
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to load config'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const update = async (patch: Partial<AdminConfig>) => {
    if (!config) return
    setError('')
    try {
      const data = await adminApi.updateConfig(patch)
      setConfig(data)
      setMessage('Settings saved')
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to save settings'))
    }
  }

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (passwords.new !== passwords.confirm) {
      setError('New passwords do not match')
      return
    }
    setPwdLoading(true)
    setError('')
    try {
      await adminApi.changePassword(passwords.current, passwords.new)
      setMessage('Password changed successfully')
      setPasswords({ current: '', new: '', confirm: '' })
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to change password'))
    } finally {
      setPwdLoading(false)
    }
  }

  if (isLoading || !config) {
    return <div className="p-8 text-center text-gray-500">Loading settings…</div>
  }

  return (
    <div className="space-y-6">
      {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
      {message && <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm">{message}</div>}

      <div className="bg-white rounded-xl border shadow-sm p-6 space-y-4">
        <h3 className="text-lg font-semibold">LLM Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default provider</label>
            <select
              value={config.default_llm_provider}
              onChange={(e) => update({ default_llm_provider: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="gemini">Gemini</option>
              <option value="deepseek">DeepSeek</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Temperature</label>
            <input
              type="number"
              step={0.1}
              min={0}
              max={2}
              value={config.temperature}
              onChange={(e) => update({ temperature: Number(e.target.value) })}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
          <ModelManager
            title="Gemini models"
            list={parseModelList(config.gemini_model, config.gemini_models)}
            activeModel={config.gemini_model}
            onChange={(list, active) =>
              update({ gemini_model: active, gemini_models: list.join(', ') })
            }
          />
          <ModelManager
            title="DeepSeek models"
            list={parseModelList(config.deepseek_model, config.deepseek_models)}
            activeModel={config.deepseek_model}
            onChange={(list, active) =>
              update({ deepseek_model: active, deepseek_models: list.join(', ') })
            }
          />
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Confidence threshold</label>
            <input
              type="number"
              step={0.05}
              min={0}
              max={1}
              value={config.confidence_threshold}
              onChange={(e) => update({ confidence_threshold: Number(e.target.value) })}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
          <div className="md:col-span-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.enable_thinking}
                onChange={(e) => update({ enable_thinking: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm font-medium text-gray-700">Enable LLM thinking / reasoning mode</span>
            </label>
            <p className="mt-1 text-xs text-gray-500">
              Off is recommended — much faster and cheaper, especially for DeepSeek “flash” models.
              Enable only if you want the model to reason step-by-step before answering.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border shadow-sm p-6 space-y-4">
        <h3 className="text-lg font-semibold">AI Assistant Notice</h3>
        <p className="text-sm text-gray-500">
          Message displayed in the AI assistant panel. Use it to tell users which LLM is used and how their data is
          handled.
        </p>
        <textarea
          value={config.ai_privacy_notice}
          onChange={(e) => update({ ai_privacy_notice: e.target.value })}
          rows={4}
          className="w-full border rounded-lg px-3 py-2 text-sm"
        />
      </div>

      <div className="bg-white rounded-xl border shadow-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">API Keys</h3>
          <button
            onClick={() => setShowKeys((s) => !s)}
            className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-800"
          >
            {showKeys ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showKeys ? 'Hide' : 'Show'} values
          </button>
        </div>
        <p className="text-sm text-gray-500">Leave unchanged to keep the existing key. Values are encrypted on the server.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gemini API key</label>
            <input
              type={showKeys ? 'text' : 'password'}
              placeholder="AIza…"
              onBlur={(e) => {
                if (e.target.value.trim()) update({ gemini_api_key: e.target.value.trim() })
              }}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DeepSeek API key</label>
            <input
              type={showKeys ? 'text' : 'password'}
              placeholder="sk-…"
              onBlur={(e) => {
                if (e.target.value.trim()) update({ deepseek_api_key: e.target.value.trim() })
              }}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border shadow-sm p-6 space-y-4">
        <h3 className="text-lg font-semibold">Change admin password</h3>
        <form onSubmit={changePassword} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Current password</label>
            <input
              type="password"
              value={passwords.current}
              onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
            <input
              type="password"
              value={passwords.new}
              onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
            <input
              type="password"
              value={passwords.confirm}
              onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              required
            />
          </div>
          <div className="md:col-span-2">
            <Button type="submit" isLoading={pwdLoading}>
              Change password
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------- AI Feedback Tab ----------
function FeedbackTab() {
  const [rows, setRows] = useState<import('../api/admin').AiFeedbackRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [source, setSource] = useState<import('../api/admin').SubmissionManuscript | null>(null)
  const [sourceTitle, setSourceTitle] = useState('')
  const [sourceLoading, setSourceLoading] = useState(false)
  const [sourceOpen, setSourceOpen] = useState(false)

  const viewSource = async (row: import('../api/admin').AiFeedbackRow) => {
    setSourceOpen(true)
    setSourceLoading(true)
    setSource(null)
    setSourceTitle(row.file_id || row.code || 'Source text')
    try {
      setSource(await adminApi.getSubmissionManuscript(row.submission_id))
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to load source text'))
      setSourceOpen(false)
    } finally {
      setSourceLoading(false)
    }
  }

  const load = async () => {
    setIsLoading(true)
    setError('')
    try {
      setRows(await adminApi.getFeedback())
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to load feedback'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const remove = async (id: string) => {
    if (!confirm('Delete this feedback entry?')) return
    try {
      await adminApi.deleteFeedback(id)
      load()
    } catch (e: any) {
      setError(fmtErr(e, 'Failed to delete feedback'))
    }
  }

  const typeBadge = (t: string) => {
    const map: Record<string, string> = {
      wrong: 'bg-red-100 text-red-800',
      incomplete: 'bg-amber-100 text-amber-800',
      missing: 'bg-blue-100 text-blue-800',
    }
    return (
      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${map[t] || 'bg-gray-100 text-gray-700'}`}>
        {t || '—'}
      </span>
    )
  }

  const columns = [
    { key: 'file_id', header: 'File ID', cell: (r: import('../api/admin').AiFeedbackRow) => <span className="font-mono text-xs">{r.file_id || '—'}</span> },
    {
      key: 'question',
      header: 'Question',
      cell: (r: import('../api/admin').AiFeedbackRow) => (
        <div>
          <div className="font-medium">{r.question_label || r.question_name}</div>
          <div className="text-xs text-gray-400 font-mono">{r.question_name}</div>
        </div>
      ),
    },
    { key: 'type', header: 'Issue', cell: (r: import('../api/admin').AiFeedbackRow) => typeBadge(r.feedback_type) },
    {
      key: 'ai_value',
      header: 'AI / current value',
      cell: (r: import('../api/admin').AiFeedbackRow) => (
        <span className="text-gray-600" title={r.original_value}>
          {r.original_value || <span className="text-gray-300">(empty)</span>}
        </span>
      ),
    },
    {
      key: 'correct',
      header: 'Correct answer',
      cell: (r: import('../api/admin').AiFeedbackRow) => <span title={r.corrected_value}>{r.corrected_value || '—'}</span>,
    },
    {
      key: 'comment',
      header: 'Comment',
      cell: (r: import('../api/admin').AiFeedbackRow) => <span className="text-gray-600 line-clamp-2" title={r.comment}>{r.comment || '—'}</span>,
    },
    {
      key: 'when',
      header: 'When',
      cell: (r: import('../api/admin').AiFeedbackRow) => (r.created_at ? new Date(r.created_at).toLocaleString() : '—'),
    },
    {
      key: 'source',
      header: 'Source',
      className: 'whitespace-nowrap',
      cell: (r: import('../api/admin').AiFeedbackRow) => (
        <button
          onClick={() => viewSource(r)}
          className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-800 hover:underline text-sm"
        >
          <FileText className="w-4 h-4" />
          View text
        </button>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-10',
      cell: (r: import('../api/admin').AiFeedbackRow) => (
        <button onClick={() => remove(r.id)} className="p-1 text-red-600 hover:bg-red-50 rounded">
          <Trash2 className="w-4 h-4" />
        </button>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
      <p className="text-sm text-gray-600">
        User-flagged AI extraction issues — the correct answer and comments help improve future extractions.
      </p>
      <DataTable
        columns={columns}
        rows={rows}
        keyExtractor={(r) => r.id}
        isLoading={isLoading}
        emptyMessage="No feedback submitted yet."
      />

      <Modal
        open={sourceOpen}
        onClose={() => setSourceOpen(false)}
        title={`Source text — ${sourceTitle}`}
        size="xl"
        footer={
          <Button variant="secondary" onClick={() => setSourceOpen(false)}>
            Close
          </Button>
        }
      >
        {sourceLoading ? (
          <div className="p-8 text-center text-gray-500">Loading source text…</div>
        ) : !source || !source.found ? (
          <div className="p-6 text-center text-gray-500">
            No AI extraction was run for this checklist, so there is no source text to show.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700 font-medium">
                {source.input_type === 'pmc_id' ? 'PMC ID' : 'Pasted text'}
              </span>
              {source.input_type === 'pmc_id' && (
                <a
                  href={`https://www.ncbi.nlm.nih.gov/pmc/articles/${source.input_ref}/`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-600 hover:underline font-mono"
                >
                  {source.input_ref}
                </a>
              )}
              {source.manuscript_text && (
                <span className="text-gray-400">{source.manuscript_text.length.toLocaleString()} characters</span>
              )}
            </div>
            {source.manuscript_text ? (
              <pre className="whitespace-pre-wrap break-words text-xs text-gray-700 bg-gray-50 border rounded-lg p-4 max-h-[60vh] overflow-y-auto">
                {source.manuscript_text}
              </pre>
            ) : (
              <p className="text-sm text-gray-500">
                The full text wasn't captured for this extraction (it predates this feature).
                {source.input_type === 'pmc_id' && ' Use the PMC link above to open the article.'}
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}

// ---------- Backup / Restore Tab ----------
function BackupTab() {
  const [busy, setBusy] = useState<'backup' | 'restore' | null>(null)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  const downloadBackup = async () => {
    setBusy('backup')
    setError('')
    setMessage('')
    try {
      const blob = await adminApi.downloadBackup()
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
      saveBlob(blob, `ev-checklist-backup-${stamp}.zip`)
      setMessage('Backup downloaded.')
    } catch (e: any) {
      setError(fmtErr(e, 'Backup failed'))
    } finally {
      setBusy(null)
    }
  }

  const restore = async (file: File) => {
    if (
      !confirm(
        'Restore will REPLACE ALL current data (checklists, responses, questionnaire, settings, feedback) with the contents of this backup. This cannot be undone.\n\nContinue?'
      )
    ) {
      if (fileRef.current) fileRef.current.value = ''
      return
    }
    setBusy('restore')
    setError('')
    setMessage('')
    try {
      const summary = await adminApi.restoreBackup(file)
      const tableTotal = Object.values(summary.tables).reduce((a, b) => a + b, 0)
      const fileTotal = Object.values(summary.files).reduce((a, b) => a + b, 0)
      setMessage(`Restore complete — ${tableTotal} database rows and ${fileTotal} files restored.`)
    } catch (e: any) {
      setError(fmtErr(e, 'Restore failed'))
    } finally {
      setBusy(null)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <div className="space-y-6">
      {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
      {message && <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm">{message}</div>}

      <div className="bg-white rounded-xl border shadow-sm p-6 space-y-3">
        <h3 className="font-semibold flex items-center gap-2">
          <Archive className="w-5 h-5 text-brand-600" />
          Backup all data
        </h3>
        <p className="text-sm text-gray-600">
          Download a single ZIP containing the full database (checklists, responses, questionnaire, settings, secret
          codes, AI feedback and jobs), all generated HTML/PDF reports, and any LLM config overrides. Keep it somewhere
          safe — it’s everything needed to recreate this instance.
        </p>
        <Button onClick={downloadBackup} isLoading={busy === 'backup'}>
          <Download className="w-4 h-4 mr-1" />
          Download backup (.zip)
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-red-200 shadow-sm p-6 space-y-3">
        <h3 className="font-semibold flex items-center gap-2 text-red-700">
          <AlertTriangle className="w-5 h-5" />
          Restore from backup
        </h3>
        <p className="text-sm text-gray-600">
          Upload a backup ZIP to restore. This <strong>replaces all current data</strong> with the backup’s contents and
          cannot be undone. Download a fresh backup first if you’re unsure.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".zip"
          onChange={(e) => e.target.files?.[0] && restore(e.target.files[0])}
          className="hidden"
        />
        <Button variant="danger" onClick={() => fileRef.current?.click()} isLoading={busy === 'restore'}>
          <Upload className="w-4 h-4 mr-1" />
          Choose backup &amp; restore
        </Button>
      </div>
    </div>
  )
}

// ---------- Benchmark ----------
const CATEGORY_COLORS: Record<string, string> = {
  'General Information': '#4E79A7',
  'Sample Information': '#59A14F',
  'Isolation Methods': '#F28E2B',
  Characterization: '#76B7B2',
  Overall: '#333333',
}

// Metric sectors (centre angle, clockwise from top) and category bar order —
// matching the published validation_radar_chart figure.
const RADAR_METRICS: { key: 'precision' | 'recall' | 'f1'; label: string; centre: number }[] = [
  { key: 'precision', label: 'Precision', centre: 60 },
  { key: 'recall', label: 'Recall', centre: 180 },
  { key: 'f1', label: 'F1', centre: 300 },
]
const CATEGORY_BAR_ORDER = [
  'Overall',
  'General Information',
  'Sample Information',
  'Isolation Methods',
  'Characterization',
]

/**
 * Polar grouped-bar ("race-track") chart matching the publication figure:
 * three metric sectors (Precision / Recall / F1), each with one bar per field
 * category radiating from the centre, coloured by category.
 */
function RadarChart({ categories }: { categories: BenchmarkCategory[] }) {
  const size = 460
  const cx = size / 2
  const cy = size / 2
  const R = 150 // radius for 100%
  const rings = [25, 50, 75, 100]

  // Order categories to match the legend / figure; keep only those present.
  const ordered = CATEGORY_BAR_ORDER.map((name) => categories.find((c) => c.category === name)).filter(
    (c): c is BenchmarkCategory => !!c
  )

  const toPoint = (deg: number, r: number): [number, number] => {
    const a = (deg * Math.PI) / 180
    return [cx + r * Math.sin(a), cy - r * Math.cos(a)]
  }

  const slot = 120 // degrees per metric
  const groupSpan = 104 // usable angular width for the 5 bars
  const barSlot = groupSpan / ordered.length
  const barWidth = barSlot * 0.82

  const wedge = (centreDeg: number, r: number): string => {
    const a0 = centreDeg - barWidth / 2
    const a1 = centreDeg + barWidth / 2
    const [x0, y0] = toPoint(a0, r)
    const [x1, y1] = toPoint(a1, r)
    return `M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 0 1 ${x1} ${y1} Z`
  }

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} role="img" aria-label="Precision, recall and F1 by field category">
        {/* grid rings */}
        {rings.map((ring) => (
          <circle key={ring} cx={cx} cy={cy} r={(R * ring) / 100} fill="none" stroke="#e5e7eb" strokeWidth={1} />
        ))}
        {/* sector dividers */}
        {RADAR_METRICS.map((mtr) => {
          const [x, y] = toPoint(mtr.centre + slot / 2, R)
          return <line key={mtr.key} x1={cx} y1={cy} x2={x} y2={y} stroke="#f3f4f6" strokeWidth={1} />
        })}
        {/* bars */}
        {RADAR_METRICS.map((mtr) =>
          ordered.map((c, j) => {
            const value = Math.max(0, Math.min(100, c[mtr.key] as number))
            const r = (R * value) / 100
            const centreDeg = mtr.centre - groupSpan / 2 + (j + 0.5) * barSlot
            const color = CATEGORY_COLORS[c.category] || '#888'
            const [lx, ly] = toPoint(centreDeg, Math.min(r + 13, R + 16))
            return (
              <g key={mtr.key + c.category}>
                <path d={wedge(centreDeg, r)} fill={color} fillOpacity={0.92} />
                <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fontSize={9.5} fontWeight={600} className="fill-gray-700">
                  {Math.round(value)}%
                </text>
              </g>
            )
          })
        )}
        {/* metric labels */}
        {RADAR_METRICS.map((mtr) => {
          const [x, y] = toPoint(mtr.centre, R + 34)
          return (
            <text key={mtr.key} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize={16} fontWeight={700} className="fill-gray-600">
              {mtr.label}
            </text>
          )
        })}
      </svg>
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-1">
        {ordered.map((c) => (
          <span key={c.category} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: CATEGORY_COLORS[c.category] || '#888' }} />
            {c.category}
          </span>
        ))}
      </div>
    </div>
  )
}

function BenchmarkResults({ run }: { run: BenchmarkRun }) {
  const m = run.metrics
  if (!m) return null
  const overall = m.categories.find((c) => c.category === 'Overall')
  return (
    <div className="space-y-6">
      {/* headline stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Agreement', value: `${m.pct_agree}%`, sub: `${m.n_agree}/${m.n_fields} fields` },
          { label: 'Precision', value: `${overall?.precision ?? m.overall.precision}%`, sub: 'vs EV-TRACK' },
          { label: 'Recall', value: `${overall?.recall ?? m.overall.recall}%`, sub: 'vs EV-TRACK' },
          { label: 'F1 Score', value: `${overall?.f1 ?? m.overall.f1}%`, sub: 'harmonic mean' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border shadow-sm p-4">
            <div className="text-xs text-gray-500">{s.label}</div>
            <div className="text-2xl font-bold text-brand-700">{s.value}</div>
            <div className="text-xs text-gray-400">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border shadow-sm p-6">
        <h3 className="font-semibold mb-1">Extraction metric profiles by field category</h3>
        <p className="text-sm text-gray-500 mb-4">
          Precision, Recall and F1 for {run.provider}
          {m.model ? ` (${m.model})` : ''} across {m.n_papers} EV-TRACK publications
          {m.judge_provider ? `, semantic review by ${m.judge_provider}` : ''}.
        </p>
        <RadarChart categories={m.categories} />
      </div>

      {/* per-category table */}
      <div className="bg-white rounded-xl border shadow-sm p-6 overflow-x-auto">
        <h3 className="font-semibold mb-3">Per-category metrics</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="py-2 pr-4">Category</th>
              <th className="py-2 px-2">Fields</th>
              <th className="py-2 px-2">Precision</th>
              <th className="py-2 px-2">Recall</th>
              <th className="py-2 px-2">F1</th>
            </tr>
          </thead>
          <tbody>
            {m.categories.map((c) => (
              <tr key={c.category} className={`border-b last:border-0 ${c.category === 'Overall' ? 'font-semibold' : ''}`}>
                <td className="py-2 pr-4">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm mr-2 align-middle" style={{ backgroundColor: CATEGORY_COLORS[c.category] || '#888' }} />
                  {c.category}
                </td>
                <td className="py-2 px-2">{c.n}</td>
                <td className="py-2 px-2">{c.precision}%</td>
                <td className="py-2 px-2">{c.recall}%</td>
                <td className="py-2 px-2">{c.f1}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* disagreement breakdown */}
      {m.disagreement.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <h3 className="font-semibold mb-3">Disagreement breakdown</h3>
          <div className="space-y-2">
            {m.disagreement.map((d) => (
              <div key={d.type} className="flex items-center gap-3 text-sm">
                <span className="w-56 text-gray-600">{d.label}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                  <div className="h-3 bg-amber-400" style={{ width: `${Math.min(100, d.pct_of_total)}%` }} />
                </div>
                <span className="w-20 text-right text-gray-500">{d.n} ({d.pct_of_total}%)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {m.skipped_papers?.length > 0 && (
        <p className="text-xs text-gray-400">
          {m.skipped_papers.length} publication(s) skipped (not open-access / unavailable): {m.skipped_papers.join(', ')}
        </p>
      )}
    </div>
  )
}

const PROVIDER_LABEL: Record<string, string> = { deepseek: 'DeepSeek', gemini: 'Gemini' }

function BenchmarkTab() {
  const [provider, setProvider] = useState('deepseek')
  const [judge, setJudge] = useState('gemini')
  const [forceRefresh, setForceRefresh] = useState(false)
  const [models, setModels] = useState<{ gemini: string[]; deepseek: string[] }>({ gemini: [], deepseek: [] })
  const [model, setModel] = useState('')
  const [judgeModel, setJudgeModel] = useState('')
  const [run, setRun] = useState<BenchmarkRun | null>(null)
  const [history, setHistory] = useState<BenchmarkRun[]>([])
  const [error, setError] = useState('')
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const isActive = run?.status === 'pending' || run?.status === 'running'

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  const loadRun = async (id: string) => {
    try {
      const fresh = await adminApi.getBenchmark(id)
      setRun(fresh)
      if (fresh.status === 'completed' || fresh.status === 'failed') {
        stopPolling()
        adminApi.listBenchmarks().then(setHistory).catch(() => {})
      }
    } catch {
      stopPolling()
    }
  }

  const startPolling = (id: string) => {
    stopPolling()
    pollRef.current = setInterval(() => loadRun(id), 2500)
  }

  useEffect(() => {
    let cancelled = false
    adminApi
      .listBenchmarks()
      .then((runs) => {
        if (cancelled) return
        setHistory(runs)
        const latest = runs[0]
        if (latest) {
          loadRun(latest.id)
          if (latest.status === 'pending' || latest.status === 'running') startPolling(latest.id)
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
      stopPolling()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    adminApi.benchmarkModels().then(setModels).catch(() => {})
  }, [])

  // Reset the model picker to the provider's default whenever the provider (or
  // the saved model list) changes.
  useEffect(() => {
    const list = models[provider as 'gemini' | 'deepseek'] || []
    setModel(list[0] || '')
  }, [provider, models])

  useEffect(() => {
    const list = judge ? models[judge as 'gemini' | 'deepseek'] || [] : []
    setJudgeModel(list[0] || '')
  }, [judge, models])

  const start = async () => {
    setError('')
    try {
      const created = await adminApi.startBenchmark(provider, judge, {
        model,
        judge_model: judgeModel,
        force_refresh: forceRefresh,
      })
      setRun(created)
      startPolling(created.id)
      adminApi.listBenchmarks().then(setHistory).catch(() => {})
    } catch (e: any) {
      setError(fmtErr(e, 'Could not start benchmark'))
    }
  }

  const remove = async (id: string) => {
    if (!confirm('Delete this benchmark run?')) return
    try {
      await adminApi.deleteBenchmark(id)
      if (run?.id === id) setRun(null)
      setHistory((h) => h.filter((r) => r.id !== id))
    } catch (e: any) {
      setError(fmtErr(e, 'Delete failed'))
    }
  }

  const total = run?.total_papers || 0
  const done = run?.done_papers || 0
  const pct = total > 0 ? Math.round((done / total) * 100) : 0

  return (
    <div className="space-y-6">
      {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

      <div className="bg-white rounded-xl border shadow-sm p-6 space-y-4">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-brand-600" />
            Benchmark the LLM against EV-TRACK
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Runs the core AI extraction over the 35 curated EV-TRACK publications and compares the result
            against the manually-curated ground truth, then reports Precision, Recall and F1 by field category.
            A run fetches each open-access manuscript and calls the LLM, so it can take several minutes.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <label className="text-sm">
            <span className="block text-gray-600 mb-1">LLM to benchmark</span>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              disabled={isActive}
              className="border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            >
              <option value="deepseek">DeepSeek</option>
              <option value="gemini">Gemini</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-gray-600 mb-1">Model</span>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              disabled={isActive}
              className="border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            >
              {(models[provider as 'gemini' | 'deepseek'] || []).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
              {(models[provider as 'gemini' | 'deepseek'] || []).length === 0 && (
                <option value="">Provider default</option>
              )}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-gray-600 mb-1">LLM judge (semantic review)</span>
            <select
              value={judge}
              onChange={(e) => setJudge(e.target.value)}
              disabled={isActive}
              className="border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            >
              <option value="gemini">Gemini</option>
              <option value="deepseek">DeepSeek</option>
              <option value="">None (fuzzy matching only)</option>
            </select>
          </label>
          {judge && (
            <label className="text-sm">
              <span className="block text-gray-600 mb-1">Judge model</span>
              <select
                value={judgeModel}
                onChange={(e) => setJudgeModel(e.target.value)}
                disabled={isActive}
                className="border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
              >
                {(models[judge as 'gemini' | 'deepseek'] || []).map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
                {(models[judge as 'gemini' | 'deepseek'] || []).length === 0 && (
                  <option value="">Provider default</option>
                )}
              </select>
            </label>
          )}
          <Button onClick={start} isLoading={isActive}>
            <Play className="w-4 h-4 mr-1" />
            {isActive ? 'Running…' : 'Run benchmark'}
          </Button>
        </div>
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={forceRefresh}
            onChange={(e) => setForceRefresh(e.target.checked)}
            disabled={isActive}
            className="mt-0.5"
          />
          <span>
            Force fresh extraction (ignore cached results)
            <span className="block text-xs text-gray-500">
              Re-runs the selected LLM on all 35 papers instead of reusing cached extractions. Slower and
              uses API tokens — enable this after changing the extraction config, model, or provider.
            </span>
          </span>
        </label>
      </div>

      {/* progress */}
      {run && isActive && (
        <div className="bg-white rounded-xl border shadow-sm p-6 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-gray-700">
              Benchmarking {PROVIDER_LABEL[run.provider] || run.provider}…
            </span>
            <span className="text-gray-500">{total > 0 ? `${done}/${total} papers` : 'starting…'}</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
            <div
              className={`h-3 bg-brand-500 transition-all ${total === 0 ? 'animate-pulse w-1/4' : ''}`}
              style={total > 0 ? { width: `${pct}%` } : undefined}
            />
          </div>
          <p className="text-sm text-gray-500">{run.progress || 'Initialising…'}</p>
        </div>
      )}

      {run?.status === 'failed' && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg text-sm">
          Benchmark failed: {run.error_message || 'unknown error'}
        </div>
      )}

      {run?.status === 'completed' && run.metrics && <BenchmarkResults run={run} />}

      {/* history */}
      {history.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <h3 className="font-semibold mb-3">Recent runs</h3>
          <div className="divide-y">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between py-2 text-sm gap-3">
                <button
                  className={`flex-1 text-left hover:text-brand-700 ${run?.id === h.id ? 'text-brand-700 font-semibold' : ''}`}
                  onClick={() => {
                    stopPolling()
                    loadRun(h.id)
                    if (h.status === 'pending' || h.status === 'running') startPolling(h.id)
                  }}
                >
                  <span className="font-medium">{PROVIDER_LABEL[h.provider] || h.provider}</span>
                  {h.model && <span className="text-gray-400"> · {h.model}</span>}
                  {h.judge_provider && <span className="text-gray-400"> · judge {PROVIDER_LABEL[h.judge_provider] || h.judge_provider}</span>}
                  <span className="text-gray-400"> · {h.created_at ? new Date(h.created_at).toLocaleString() : ''}</span>
                </button>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    h.status === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : h.status === 'failed'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {h.status}
                </span>
                <button onClick={() => remove(h.id)} className="text-gray-400 hover:text-red-600" title="Delete run">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------- Bulk PMC Ingest Tab ----------
function BulkPmcTab() {
  const [input, setInput] = useState('')
  const [provider, setProvider] = useState('gemini')
  const [run, setRun] = useState<BulkPmcRun | null>(null)
  const [history, setHistory] = useState<BulkPmcRun[]>([])
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const isActive = run?.status === 'pending' || run?.status === 'running'

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  const loadRun = async (id: string) => {
    try {
      const fresh = await adminApi.getBulkPmc(id)
      setRun(fresh)
      if (fresh.status === 'completed' || fresh.status === 'failed') {
        stopPolling()
        adminApi.listBulkPmc().then(setHistory).catch(() => {})
      }
    } catch {
      stopPolling()
    }
  }

  const startPolling = (id: string) => {
    stopPolling()
    pollRef.current = setInterval(() => loadRun(id), 2500)
  }

  useEffect(() => {
    let cancelled = false
    adminApi
      .listBulkPmc()
      .then((runs) => {
        if (cancelled) return
        setHistory(runs)
        const latest = runs[0]
        if (latest) {
          loadRun(latest.id)
          if (latest.status === 'pending' || latest.status === 'running') startPolling(latest.id)
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
      stopPolling()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const parseIds = (text: string): string[] => {
    const ids: string[] = []
    for (const line of text.split(/[\n,;\t]+/)) {
      const raw = line.trim().toUpperCase()
      if (!raw) continue
      const m = raw.match(/(?:PMC)?(\d{4,12})/)
      if (m) ids.push(`PMC${m[1]}`)
    }
    return [...new Set(ids)]
  }

  const start = async () => {
    setError('')
    const ids = parseIds(input)
    if (ids.length === 0) {
      setError('Paste at least one valid PMC ID.')
      return
    }
    setIsLoading(true)
    try {
      const created = await adminApi.startBulkPmc(ids, provider)
      setRun(created)
      startPolling(created.id)
      adminApi.listBulkPmc().then(setHistory).catch(() => {})
    } catch (e: any) {
      setError(fmtErr(e, 'Could not start bulk PMC ingestion'))
    } finally {
      setIsLoading(false)
    }
  }

  const remove = async (id: string) => {
    if (!confirm('Delete this bulk PMC run?')) return
    try {
      await adminApi.deleteBulkPmc(id)
      if (run?.id === id) setRun(null)
      setHistory((h) => h.filter((r) => r.id !== id))
    } catch (e: any) {
      setError(fmtErr(e, 'Delete failed'))
    }
  }

  const total = run?.total || 0
  const done = run?.done || 0
  const pct = total > 0 ? Math.round((done / total) * 100) : 0

  return (
    <div className="space-y-6">
      {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

      <div className="bg-white rounded-xl border shadow-sm p-6 space-y-4">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-brand-600" />
            Bulk PMC ingestion
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Paste a list of PMC IDs. For each open-access article the backend will fetch the full text, check for
            duplicates by PMC ID / DOI / title, run the AI extraction, and auto-submit a new checklist.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">PMC IDs</label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={8}
              placeholder="PMC1234567&#10;PMC7654321&#10;PMC9876543"
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
              disabled={isActive}
            />
            <p className="text-xs text-gray-500 mt-1">{parseIds(input).length} unique ID(s) parsed.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">LLM provider</label>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              disabled={isActive}
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            >
              <option value="gemini">Gemini</option>
              <option value="deepseek">DeepSeek</option>
            </select>
          </div>
        </div>

        <Button onClick={start} isLoading={isLoading} disabled={isActive}>
          <Play className="w-4 h-4 mr-1" />
          {isActive ? 'Running…' : 'Start bulk ingestion'}
        </Button>
      </div>

      {run && isActive && (
        <div className="bg-white rounded-xl border shadow-sm p-6 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-gray-700">Bulk ingestion running…</span>
            <span className="text-gray-500">{total > 0 ? `${done}/${total} articles` : 'starting…'}</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
            <div
              className={`h-3 bg-brand-500 transition-all ${total === 0 ? 'animate-pulse w-1/4' : ''}`}
              style={total > 0 ? { width: `${pct}%` } : undefined}
            />
          </div>
          <p className="text-sm text-gray-500">{run.progress || 'Initialising…'}</p>
        </div>
      )}

      {run?.status === 'failed' && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg text-sm">
          Bulk ingestion failed: {run.error_message || 'unknown error'}
        </div>
      )}

      {run && (run.status === 'completed' || run.status === 'failed') && run.results.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm p-6 space-y-3">
          <h3 className="font-semibold">Results</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium text-gray-700">PMC ID</th>
                  <th className="px-3 py-2 font-medium text-gray-700">Title</th>
                  <th className="px-3 py-2 font-medium text-gray-700">Status</th>
                  <th className="px-3 py-2 font-medium text-gray-700">File ID</th>
                  <th className="px-3 py-2 font-medium text-gray-700">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {run.results.map((item: BulkPmcItem) => (
                  <tr key={item.pmc_id}>
                    <td className="px-3 py-2 font-mono">{item.pmc_id}</td>
                    <td className="px-3 py-2 max-w-xs truncate" title={item.title}>{item.title}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.status === 'created'
                            ? 'bg-green-100 text-green-800'
                            : item.status === 'duplicate'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono">{item.file_id || '—'}</td>
                    <td className="px-3 py-2 text-red-600 text-xs">{item.error || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <h3 className="font-semibold mb-3">Recent runs</h3>
          <div className="divide-y">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between py-2 text-sm gap-3">
                <button
                  className={`flex-1 text-left hover:text-brand-700 ${run?.id === h.id ? 'text-brand-700 font-semibold' : ''}`}
                  onClick={() => {
                    stopPolling()
                    loadRun(h.id)
                    if (h.status === 'pending' || h.status === 'running') startPolling(h.id)
                  }}
                >
                  <span className="font-medium">{h.provider}</span>
                  <span className="text-gray-400"> · {h.total} IDs</span>
                  <span className="text-gray-400"> · {h.created_at ? new Date(h.created_at).toLocaleString() : ''}</span>
                </button>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    h.status === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : h.status === 'failed'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {h.status}
                </span>
                <button onClick={() => remove(h.id)} className="text-gray-400 hover:text-red-600" title="Delete run">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------- Dashboard shell ----------
const tabs = [
  { key: 'usage', label: 'Usage', icon: Activity },
  { key: 'responses', label: 'Responses', icon: FileText },
  { key: 'questionnaire', label: 'Questionnaire', icon: ClipboardList },
  { key: 'reports', label: 'Reports', icon: RefreshCw },
  { key: 'report-preview', label: 'Report Preview', icon: FileSearch },
  { key: 'extraction-config', label: 'LLM Config', icon: Sliders },
  { key: 'feedback', label: 'AI Feedback', icon: Flag },
  { key: 'bulk-pmc', label: 'Bulk PMC', icon: BookOpen },
  { key: 'benchmark', label: 'Benchmark', icon: BarChart3 },
  { key: 'backup', label: 'Backup', icon: Archive },
  { key: 'settings', label: 'Settings', icon: Settings },
]

function Dashboard() {
  const { logout } = useAuth()
  const navigate = useNavigate()
  const activeTab = useTabRoute('/admin')

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <LayoutDashboard className="w-7 h-7 text-brand-600" />
          <h2 className="text-2xl font-bold">Admin Dashboard</h2>
        </div>
        <Button variant="ghost" onClick={() => { logout(); navigate('/admin') }}>
          <LogOut className="w-4 h-4 mr-1" />
          Logout
        </Button>
      </div>

      <div className="flex flex-wrap gap-x-2 gap-y-1 border-b mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon
          const isActive = activeTab === tab.key
          return (
            <Link
              key={tab.key}
              to={`/admin/${tab.key}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                isActive
                  ? 'text-brand-700 border-brand-700'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </Link>
          )
        })}
      </div>

      <Outlet />
    </div>
  )
}

export default function AdminPage() {
  const { isAuthenticated } = useAuth()
  return (
    <Routes>
      <Route path="/" element={isAuthenticated ? <Navigate to="/admin/responses" replace /> : <LoginForm />} />
      <Route path="/*" element={isAuthenticated ? <Dashboard /> : <LoginForm />}>
        <Route path="usage" element={<UsageTab />} />
        <Route path="responses" element={<ResponsesTab />} />
        <Route path="questionnaire" element={<QuestionnaireTab />} />
        <Route path="reports" element={<ReportsTab />} />
        <Route path="report-preview" element={<ReportPreviewTab />} />
        <Route path="extraction-config" element={<ExtractionConfigTab />} />
        <Route path="feedback" element={<FeedbackTab />} />
        <Route path="bulk-pmc" element={<BulkPmcTab />} />
        <Route path="benchmark" element={<BenchmarkTab />} />
        <Route path="backup" element={<BackupTab />} />
        <Route path="settings" element={<SettingsTab />} />
        <Route path="*" element={<Navigate to="/admin/responses" replace />} />
      </Route>
    </Routes>
  )
}
